import React from 'react';
import {
  Alert, Col, Descriptions, Row, Space, Statistic, Typography, theme,
} from 'antd';

const { Text } = Typography;

const pesos = (v) =>
  `$${Number(v || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const MOTIVOS = {
  cadena_rota: 'La conciliación tiene un mes sin calcular, así que no se sabe cuánto saldo a favor queda.',
  sin_conciliar: 'Falta conciliar el histórico de este contribuyente hasta el mes anterior.',
};

/**
 * IVA del periodo aplicado contra el saldo a favor ACUMULADO de la conciliación.
 *
 * Responde la pregunta que se hace de verdad —«¿cuánto tiene que pagar este
 * mes?»— y no «cuánto IVA generó el mes», que es otra cosa: mientras quede
 * acumulado, un mes a cargo se paga en 0.
 *
 * PRESENTACIONAL A PROPÓSITO. No consulta nada: el padre trae el dato con el
 * mismo botón que la estimación, porque este cálculo consulta documentos a SW y
 * cargarlo solo al abrir la ficha cobraría esa consulta en cada visita.
 *
 * DOS CIFRAS QUE PUEDEN NO COINCIDIR, y hay que decirlo en pantalla: el IVA
 * acreditable de aquí sale de la regla de la conciliación (recibidos de tipo I
 * neto de notas de crédito G02), no de la política por actividad que usa la
 * estimación de arriba. Son dos reglas conviviendo mientras esto se valida.
 */
const IvaAcumuladoPanel = ({ datos }) => {
  const { token } = theme.useToken();

  if (!datos) return null;

  if (datos.ok === false) {
    return <Alert type="warning" showIcon message={datos.motivo || 'No se pudo calcular'} />;
  }

  const periodo = datos.periodo || {};
  const acumulado = datos.acumulado;
  const aplicado = datos.aplicado;
  const aFavorDelMes = Number(periodo.saldo || 0) < 0;

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      {/* Sin acumulado confiable no se aplica NADA: un acumulado incompleto deja el
          IVA por pagar por debajo del real, y eso sugiere no pagar cuando sí hay
          que pagar. */}
      {!acumulado && (
        <Alert
          type="warning"
          showIcon
          message="No se puede aplicar saldo a favor de periodos anteriores"
          description={
            <Space direction="vertical" size={2} style={{ width: '100%' }}>
              <Text>{MOTIVOS[datos.motivo] || 'La cadena de la conciliación no está disponible.'}</Text>
              {!!datos.cadena?.rota_en && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Se corta en {datos.cadena.rota_en}. Calcúlalo en «Conciliación de IVA», más abajo.
                </Text>
              )}
            </Space>
          }
        />
      )}

      <Row gutter={[12, 12]}>
        <Col xs={24} sm={8}>
          <Statistic
            title={aFavorDelMes ? 'IVA a favor del periodo' : 'IVA a cargo del periodo'}
            value={pesos(Math.abs(Number(periodo.saldo || 0)))}
            valueStyle={{ color: aFavorDelMes ? token.colorSuccess : undefined }}
          />
        </Col>
        <Col xs={24} sm={8}>
          <Statistic
            title="Saldo a favor acumulado"
            value={acumulado ? pesos(acumulado.disponible) : '—'}
            valueStyle={acumulado ? { color: token.colorSuccess } : undefined}
          />
        </Col>
        <Col xs={24} sm={8}>
          <Statistic
            title="IVA por pagar"
            value={aplicado ? pesos(aplicado.iva_por_pagar) : '—'}
            valueStyle={{
              color: aplicado && Number(aplicado.iva_por_pagar || 0) > 0
                ? token.colorError
                : token.colorSuccess,
            }}
          />
        </Col>
      </Row>

      <Descriptions bordered size="small" column={{ xs: 1, sm: 2 }}>
        <Descriptions.Item label="IVA trasladado del periodo">
          {pesos(periodo.iva_trasladado)}
        </Descriptions.Item>
        <Descriptions.Item label="IVA acreditable del periodo">
          {pesos(periodo.iva_acreditable)}
        </Descriptions.Item>
        {acumulado && (
          <Descriptions.Item label={`Acumulado al cierre de ${acumulado.al_cierre_de}`}>
            {pesos(acumulado.disponible)}
          </Descriptions.Item>
        )}
        {aplicado && (
          <Descriptions.Item label="Acreditado en el periodo">
            {pesos(aplicado.acreditado)}
          </Descriptions.Item>
        )}
        {aplicado && (
          <Descriptions.Item label="Saldo a favor que queda">
            {pesos(aplicado.acumulado_resultante)}
          </Descriptions.Item>
        )}
        <Descriptions.Item label="CFDI que respaldan el periodo">
          {Number(periodo.documentos || 0)}
        </Descriptions.Item>
      </Descriptions>

      <Space direction="vertical" size={2} style={{ width: '100%' }}>
        {!!periodo.parcial && (
          <Text type="secondary" style={{ fontSize: 12 }}>
            El periodo no ha cerrado: la cifra es parcial y va a cambiar conforme lleguen CFDI.
          </Text>
        )}
        <Text type="secondary" style={{ fontSize: 12 }}>
          El IVA acreditable de este bloque usa la regla de la conciliación (CFDI recibidos de tipo
          Ingreso, neto de notas de crédito G02), que no es la que usa la estimación de arriba: las
          dos cifras pueden no coincidir mientras esto se valida.
        </Text>
        <Text type="secondary" style={{ fontSize: 12 }}>
          Estimación informativa. El resultado del periodo no se guarda. El acumulado supone que se
          acreditó todo lo posible cada mes y no contempla devoluciones ni compensaciones: si se
          recuperó un saldo por otra vía, el acumulado queda por encima del real.
        </Text>
      </Space>
    </Space>
  );
};

export default IvaAcumuladoPanel;
